import { usePosSalesByCurrencyReportQuery } from "@/modules/merchant/presentation/api/queries";
import { EMPTY_POS_SALES_CURRENCY_QUERY } from "@/modules/merchant/presentation/lib/pos-sales-currency-report-query";
import { SHOP_CURRENCIES, formatPrice } from "@/shared/lib/currency";
import { Card, CardContent, Loader } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { BarChart3 } from "lucide-react";

const REPORTS_ROUTE = "/app/shops/$shopId/reports" as const;

/**
 * B — ສະຫຼຸບຍອດ 3 ສະກຸນຈາກ cache ລາຍງານ (query ວ່າງ = ຊ່ວງ/ສາຂາຕາມສິດເຫມືອນສ່ວນລຸ່ມເມື່ອຍັງບໍ່ກອງ)
 */
export function ShopDashboardSalesSummaryStrip({
  shopId,
  canView,
}: {
  shopId: string | undefined;
  canView: boolean;
}) {
  const report = usePosSalesByCurrencyReportQuery(
    shopId,
    EMPTY_POS_SALES_CURRENCY_QUERY,
    canView,
  );

  if (!canView) {
    return null;
  }

  const accents: [string, string][] = [
    ["border-t-primary", "from-primary/[0.06]"],
    ["border-t-emerald-500", "from-emerald-500/[0.06]"],
    ["border-t-amber-500", "from-amber-500/[0.06]"],
  ];

  return (
    <Card className="mb-6 overflow-hidden rounded-2xl border-border/80 shadow-sm">
      <CardContent className="space-y-4 py-5 sm:py-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-heading font-semibold text-base text-foreground">
              ສະຫຼຸບຍອດຂາຍ (ລວມ)
            </h2>
            <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
              ຕາມສະກຸນບິນ — ບໍ່ແປງ FX. ໃຊ້ຊຸດຂໍ້ມູນດຽວກັບຕາຕະລາງລຸ່ມເມື່ອຍັງບໍ່ກອງ.
            </p>
          </div>
          <Link
            to={REPORTS_ROUTE}
            params={{ shopId: shopId ?? "" }}
            className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-3 py-1.5 text-primary text-sm font-medium transition-colors hover:bg-primary/10"
          >
            <BarChart3 className="size-4" aria-hidden />
            ເບິ່ງລາຍງານເຕັມ
          </Link>
        </div>
        {report.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : report.error ? (
          <p className="text-destructive text-sm">ໂຫຼດສະຫຼຸບບໍ່ສຳເລັດ</p>
        ) : report.data ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {SHOP_CURRENCIES.map((c, i) => {
              const [top, grad] = accents[i] ?? accents[0]!;
              const bucket = report.data!.byCurrency[c] ?? {
                total: 0,
                saleCount: 0,
              };
              return (
                <div
                  key={c}
                  className={`relative overflow-hidden rounded-xl border border-border/80 bg-linear-to-b ${grad} to-card/90 px-4 py-3.5 shadow-sm ring-1 ring-border/30 border-t-2 ${top}`}
                >
                  <p className="font-mono text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                    {c}
                  </p>
                  <p className="mt-1 font-bold font-heading text-foreground text-lg tabular-nums tracking-tight">
                    {formatPrice(bucket.total, c)}
                  </p>
                  <p className="mt-0.5 text-muted-foreground text-xs">
                    {bucket.saleCount} ບິນ
                  </p>
                </div>
              );
            })}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
